import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import {
  Plus,
  Users,
  X,
  ArrowRight,
  Copy as CopyIcon,
  Lightning,
  ShieldCheck,
} from "@phosphor-icons/react";

function CreateSquadModal({ onClose, onCreated }) {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [planId, setPlanId] = useState("");
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/plans").then((r) => {
      setPlans(r.data || []);
      if (r.data && r.data.length > 0) setPlanId(r.data[0].id);
    });
  }, []);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/squads", { name, description, plan_id: planId });
      onCreated(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  const valid = name.trim().length >= 2 && planId;

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border p-4 flex items-center justify-between">
          <div className="font-display text-2xl font-black uppercase">Create Squad</div>
          <button onClick={onClose} data-testid="create-squad-close"><X size={20} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="label-mono mb-1">Squad Name</div>
            <input
              autoFocus
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Alpha Squad"
              className="w-full bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
              data-testid="create-squad-name"
            />
          </div>
          <div>
            <div className="label-mono mb-1">Description (optional)</div>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows="2"
              placeholder="What you're training for..."
              className="w-full bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt"
              data-testid="create-squad-desc"
            />
          </div>
          <div>
            <div className="label-mono mb-1">Anchor Plan</div>
            {plans.length === 0 ? (
              <div className="border border-border bg-background p-3 text-sm text-muted-foreground">
                You need to build a plan first.{" "}
                <Link to="/builder" className="text-volt hover:underline">Open Plan Builder →</Link>
              </div>
            ) : (
              <select
                value={planId}
                onChange={(e) => setPlanId(e.target.value)}
                className="w-full bg-secondary border border-border px-3 py-2"
                data-testid="create-squad-plan"
              >
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            )}
            <div className="text-xs text-muted-foreground mt-1">
              Members get an auto-tuned copy of this plan when they join.
            </div>
          </div>
          {err && <div className="border border-destructive text-destructive px-3 py-2 text-sm">{err}</div>}
          <button
            disabled={!valid || busy}
            onClick={submit}
            className="w-full bg-volt text-black py-3 font-display uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50"
            data-testid="create-squad-submit"
          >
            {busy ? "Creating..." : "Create Squad"}
          </button>
        </div>
      </div>
    </div>
  );
}

function JoinSquadModal({ onClose, onJoined }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/squads/join", { invite_code: code.trim() });
      onJoined(data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 grid place-items-center p-4" onClick={onClose}>
      <div className="bg-card border border-border w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="border-b border-border p-4 flex items-center justify-between">
          <div className="font-display text-2xl font-black uppercase">Join Squad</div>
          <button onClick={onClose} data-testid="join-squad-close"><X size={20} weight="bold" /></button>
        </div>
        <div className="p-5 space-y-4">
          <div>
            <div className="label-mono mb-1">Invite Code</div>
            <input
              autoFocus
              value={code}
              onChange={(e) => setCode(e.target.value.toUpperCase())}
              placeholder="e.g. ALPHA123"
              className="w-full bg-secondary border border-border px-3 py-2 focus:outline-none focus:border-volt font-display tracking-widest text-lg uppercase"
              data-testid="join-squad-code"
            />
            <div className="text-xs text-muted-foreground mt-1">
              The squad's plan will auto-copy to your dossier and rescale to your 1RMs.
            </div>
          </div>
          {err && <div className="border border-destructive text-destructive px-3 py-2 text-sm">{err}</div>}
          <button
            disabled={code.length < 4 || busy}
            onClick={submit}
            className="w-full bg-volt text-black py-3 font-display uppercase tracking-widest font-bold hover:bg-white disabled:opacity-50"
            data-testid="join-squad-submit"
          >
            {busy ? "Joining..." : "Join Squad"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function MySquads() {
  const navigate = useNavigate();
  const [squads, setSquads] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showJoin, setShowJoin] = useState(false);

  const refresh = () => api.get("/squads/me").then((r) => setSquads(r.data));
  useEffect(() => { refresh(); }, []);

  return (
    <div data-testid="my-squads-tab">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <div className="text-sm text-muted-foreground max-w-2xl">
          Build a private team around a shared plan. Track everyone's progress on the same mission.
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setShowJoin(true)}
            data-testid="open-join-squad"
            className="px-4 py-2 border border-border font-display uppercase tracking-widest text-xs hover:border-volt hover:text-volt inline-flex items-center gap-2"
          >
            <Users size={14} weight="bold" /> Join with Code
          </button>
          <button
            onClick={() => setShowCreate(true)}
            data-testid="open-create-squad"
            className="px-4 py-2 bg-volt text-black font-display uppercase tracking-widest text-xs hover:bg-white inline-flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> Create Squad
          </button>
        </div>
      </div>

      {squads === null ? (
        <div className="label-mono animate-pulse">LOADING...</div>
      ) : squads.length === 0 ? (
        <div className="border border-dashed border-border p-12 text-center">
          <ShieldCheck size={32} weight="bold" className="text-volt mx-auto mb-3" />
          <div className="label-mono mb-3">NO SQUADS YET</div>
          <p className="text-muted-foreground mb-6 max-w-md mx-auto text-sm">
            Train with friends, family, or your unit. Create a squad anchored to one of your plans, share the invite code, and watch each member's progress on the same mission.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {squads.map((s) => (
            <Link
              to={`/squads/${s.id}`}
              key={s.id}
              className="tactical-card p-5 flex flex-col h-full"
              data-testid={`squad-card-${s.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className="label-mono text-volt">// SQUAD</div>
                <div className="label-mono">{s.member_count} OPERATORS</div>
              </div>
              <h3 className="font-display text-2xl font-black leading-tight mb-2">{s.name}</h3>
              {s.description && <p className="text-xs text-muted-foreground mb-3 line-clamp-2">{s.description}</p>}
              <div className="text-xs text-muted-foreground mb-4">PLAN: {s.source_plan_name}</div>
              <div className="mt-auto flex items-center justify-between">
                <div className="border border-volt/40 px-2 py-1 font-display tracking-widest text-xs text-volt" data-testid={`squad-invite-${s.id}`}>
                  {s.invite_code}
                </div>
                <div className="inline-flex items-center gap-1 label-mono hover:text-volt">
                  Open <ArrowRight size={12} weight="bold" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <CreateSquadModal
          onClose={() => setShowCreate(false)}
          onCreated={(s) => { setShowCreate(false); navigate(`/squads/${s.id}`); }}
        />
      )}
      {showJoin && (
        <JoinSquadModal
          onClose={() => setShowJoin(false)}
          onJoined={(s) => { setShowJoin(false); navigate(`/squads/${s.id}`); }}
        />
      )}
    </div>
  );
}
